/** One filter group. `getKeys` returns every option a row belongs to (several for a flags group). */
export type FacetDimension<T> = {
	id: string;
	getKeys: (row: T) => readonly string[];
};

export const NOT_SET_KEY = "__not_set__";

// DEFAULT: a blank value is its own option (NOT_SET_KEY) rather than being dropped from the facet.
export const valueOrNotSet = (value: string | null): readonly string[] => [value ?? NOT_SET_KEY];

/** Selected option ids per dimension id; an empty set means the group isn't narrowing. */
export type FacetSelection = Readonly<Record<string, ReadonlySet<string>>>;

export type FacetFilterResult<T> = {
	/** OR within a group, AND across groups and the predicate. */
	rows: T[];
	/** Exclude-own-group counts: what each option would match given every OTHER group's selection. */
	counts: Record<string, Map<string, number>>;
};

const matchesDimension = <T>(row: T, dimension: FacetDimension<T>, selected: ReadonlySet<string> | undefined): boolean =>
	!selected || selected.size === 0 || dimension.getKeys(row).some((key) => selected.has(key));

/** Filtered rows plus live facet counts in one pass; `predicate` narrows without being a facet. */
export function applyFacetFilters<T>(
	rows: readonly T[],
	dimensions: readonly FacetDimension<T>[],
	selection: FacetSelection,
	predicate: (row: T) => boolean,
): FacetFilterResult<T> {
	const counts: Record<string, Map<string, number>> = Object.fromEntries(dimensions.map((d) => [d.id, new Map<string, number>()]));
	const filtered: T[] = [];
	for (const row of rows) {
		if (!predicate(row)) continue;
		const matches = dimensions.map((d) => matchesDimension(row, d, selection[d.id]));
		const failing = matches.filter((m) => !m).length;
		if (failing === 0) filtered.push(row);
		// A row counts toward a group when every OTHER group matches: it fails none, or only this one.
		if (failing > 1) continue;
		dimensions.forEach((dimension, index) => {
			if (failing === 1 && matches[index]) return;
			const dimensionCounts = counts[dimension.id];
			// DEFAULT: an option's first matching row starts its tally at 0.
			for (const key of dimension.getKeys(row)) dimensionCounts.set(key, (dimensionCounts.get(key) ?? 0) + 1);
		});
	}
	return { rows: filtered, counts };
}

/** Option order: by label, by key (chronological yyyy-MM), or a fixed list shown even at 0. */
export type FacetOptionOrder = "label" | "key" | readonly string[];

/** A group's options for `FacetFilterSection`, taken from the unfiltered rows so none vanish at 0. */
export function buildFacetOptions<T>(
	rows: readonly T[],
	dimension: FacetDimension<T>,
	result: FacetFilterResult<T>,
	selection: FacetSelection,
	getLabel: (key: string) => string,
	order: FacetOptionOrder,
): { id: string; label: string; count: number; checked: boolean }[] {
	const counts = result.counts[dimension.id];
	const selected = selection[dimension.id];
	// DEFAULT: a key no row matching the other groups carries has a count of 0, not a missing one.
	const toOption = (key: string) => ({ id: key, label: getLabel(key), count: counts.get(key) ?? 0, checked: selected?.has(key) === true });
	if (Array.isArray(order)) return order.map(toOption);
	// Checked keys stay listed even when the rows no longer carry them, so they can still be unchecked.
	const keys = new Set<string>(selected);
	for (const row of rows) for (const key of dimension.getKeys(row)) keys.add(key);
	const sortField = order === "key" ? "id" : "label";
	return [...keys].map(toOption).sort((a, b) => a[sortField].localeCompare(b[sortField], undefined, { numeric: true }));
}

/** Groups narrowing the view — the "Filters (n)" badge. */
export const countActiveFacets = (selection: FacetSelection): number => Object.values(selection).filter((selected) => selected.size > 0).length;
